/**
 * Card art URLs (research 03 §2; owner decision Q3: Riot CDN art **and** our own R2 mirror).
 *
 * Two sources per printing, tried in order:
 * 1. the mirror, `<mirrorBase>/<printingId>-<width>.webp` (built into R2 by a later job; same origin as
 *    the client, so it also works for canvas/WebGL);
 * 2. Riot's CDN (a Sanity pipeline): the printing's `image_url` without its query, plus
 *    `?accountingTag=RB&w=<width>&fm=webp` (format must be explicit: the CDN does no content negotiation).
 *
 * The base is configurable so environments (local, preview, production) and tests can point anywhere,
 * or disable the mirror (`mirrorBase: null`) until it exists.
 */
import type { CatalogPrinting } from './schema';

/** Widths in use: board (240), hand / 2× board (372), inspector zoom (744, native). */
export const ART_WIDTHS = [240, 372, 744] as const;
export type ArtWidth = (typeof ART_WIDTHS)[number];

/** Where art comes from: the R2 mirror base (or `null`) and the Riot CDN quality. */
export interface ArtConfig {
  /** Mirror base URL without a trailing slash, or `null` to use Riot's CDN only. */
  mirrorBase: string | null;
  /** WebP quality passed to Riot's CDN (`q`), or `null` for the CDN default. */
  riotQuality: number | null;
}

export const DEFAULT_ART_CONFIG: ArtConfig = {
  mirrorBase: 'https://play.runebound.gg/art',
  riotQuality: 80,
};

/** Riot CDN URL for a printing image at `width` (WebP). */
export function riotArtUrl(
  imageUrl: string,
  width: ArtWidth,
  quality: number | null = DEFAULT_ART_CONFIG.riotQuality,
): string {
  const base = imageUrl.split('?')[0] ?? imageUrl;
  const q = quality === null ? '' : `&q=${quality}`;
  return `${base}?accountingTag=RB&w=${width}&fm=webp${q}`;
}

/** Mirror URL for a printing at `width`: `<base>/<printingId>-<width>.webp`. */
export function mirrorArtUrl(printingId: string, width: ArtWidth, base: string): string {
  return `${base.replace(/\/+$/, '')}/${encodeURIComponent(printingId)}-${width}.webp`;
}

/** The art URLs to try for a printing, best first (mirror, then Riot's CDN). */
export function artSources(
  printing: Pick<CatalogPrinting, 'id' | 'imageUrl'>,
  width: ArtWidth,
  config: ArtConfig = DEFAULT_ART_CONFIG,
): string[] {
  const riot = riotArtUrl(printing.imageUrl, width, config.riotQuality);
  return config.mirrorBase === null ? [riot] : [mirrorArtUrl(printing.id, width, config.mirrorBase), riot];
}

/** Battlefields are the only landscape cards (`…-1039x744.png`); everything else is 744×1039 portrait. */
export function isLandscapeImage(imageUrl: string): boolean {
  const match = /-(\d+)x(\d+)\.\w+(?:\?|$)/.exec(imageUrl);
  if (!match) return false;
  return Number(match[1]) > Number(match[2]);
}

/** The smallest configured width that covers `cssPixels × devicePixelRatio`. */
export function pickArtWidth(cssPixels: number, devicePixelRatio = 1): ArtWidth {
  const needed = cssPixels * devicePixelRatio;
  return ART_WIDTHS.find((w) => w >= needed) ?? 744;
}
